import React, { FC } from 'react';
import { FormControl, InputLabel, OutlinedInput, FormHelperText } from '@mui/material';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

export interface ITextInputProps {
  label: string;
  handleChange: (value: any) => void;
  value?: string | number | undefined;
  hasError?: boolean;
  errorText?: string;
  helperText?: string;
  required?: boolean;
  placeholderText?: string;
  inputType?: string;
  endAdornment?: React.ReactNode;
  inputProps?: Record<string, string>;
  isDisabled?: boolean;
  tooltipText?: string;
}

/**
 * Represents a TextField where users can input Text or Numbers.
 *
 * @param props - The properties for TextInput
 * @param props.handleChange - Callback function that will be used when value changes.
 * @param props.value - Value that controls the component.
 * @param props.hasError - Boolean value to display error
 * @param props.errorText - Error message when the TextField has an error.
 * @param props.helperText - Message to give user context of what the TextInput is used for.
 * @param props.placeholderText - Message when TextInput is empty.
 * @param props.inputType - The data type the TextField accepts.
 * @param props.endAdornment - Icon that is shown at the end of the text field. For example, a Percentage symbol.
 * @param props.inputProps - Optional Options for Input from MaterialUI
 * @param props.isDisabled - Boolean value to disable/enable TextInput
 * @param param0
 * @returns
 */

const TextInput: FC<ITextInputProps> = ({
  label,
  handleChange,
  value,
  helperText,
  hasError,
  errorText,
  placeholderText,
  inputType,
  inputProps,
  endAdornment,
  isDisabled,
  tooltipText,
}) => {
  return (
    <div>
      {tooltipText && (
        <div className="flex items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <InfoTooltip text={tooltipText} placement="right" />
        </div>
      )}
      <FormControl error={hasError} sx={{ my: 1, width: `100%` }}>
        <InputLabel shrink={true} htmlFor={label}>
          {label}
        </InputLabel>
        <OutlinedInput
          id={label}
          label={label}
          value={value}
          notched={true}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholderText}
          type={inputType}
          endAdornment={endAdornment}
          inputProps={inputProps}
          role="textbox"
          disabled={isDisabled}
        />
        {hasError && errorText && <FormHelperText>{errorText}</FormHelperText>}
        {helperText && !hasError && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </div>
  );
};

export default TextInput;
